import React, { useMemo } from "react";
import { type ColumnDef } from "@tanstack/react-table";
import { DataTable } from "@/components/ui/data-table";
import {
  MINI_STAT_COUNT_GRADIENT,
  MINI_STAT_LOSS_GRADIENT,
  MINI_STAT_WIN_GRADIENT,
} from "@/utils/miniStatGradient";
import { cn } from "@/lib/utils";
import {
  formatStrategyRdBarsToEvent,
  formatStrategyRdTimeToExit,
  formatStrategyRdExcursionSummary,
  formatStrategyRdLeverage,
  formatStrategyRdMoney,
  formatStrategyRdPct,
  formatStrategyRdRewardRisk,
  formatStrategyRdRMultiple,
  formatStrategyRdWinRate,
  strategyRdMoneyCurrency,
} from "../../../utils/strategyRd";
import type { StrategyRdApplyOverview, StrategyRdMarginSummary } from "../../../types/strategyRd";
import {
  PNL_NEGATIVE_COLOR,
  PNL_POSITIVE_COLOR,
} from "../Statistics/components/statsSignedPnlBarUtils";
import { StatisticsDefinitionTooltip } from "../Statistics/components/statisticsChartTooltip";
import TradeLogMiniStatistics from "../TradeLog/components/TradeLogMiniStatistics";
import { StrategyRdSimCharts } from "./StrategyRdSimCharts";
import { strategyRdMetaClassName } from "./strategyRdCardShell";
import { strategyRdCardTitleClassName, strategyRdSectionCardClassName } from "./strategyRdLabUi";

export type StrategyRdHeroCard = HeroCard;

type HeroCard = {
  id: string;
  title: string;
  amount: string;
  tooltip?: string;
  backgroundGradient?: string;
};

type MetricRow = {
  id: string;
  metric: string;
  value?: string;
  usd?: string | null;
  r?: string | null;
  tooltip?: string;
  tone?: number | null;
  usdTone?: number | null;
  rTone?: number | null;
};

function toneStyle(tone: number | null | undefined): React.CSSProperties | undefined {
  if (tone == null || !Number.isFinite(tone) || tone === 0) return undefined;
  return { color: tone > 0 ? PNL_POSITIVE_COLOR : PNL_NEGATIVE_COLOR };
}

function metricCell(metric: string, tooltip?: string): JSX.Element {
  if (tooltip) {
    return <StatisticsDefinitionTooltip label={metric} description={tooltip} />;
  }
  return (
    <span className="font-[family-name:var(--font-primary)] text-sm font-medium text-gray-500 dark:text-sidebar-foreground">
      {metric}
    </span>
  );
}

function valueCell(
  value: string | null | undefined,
  tone?: number | null
): JSX.Element {
  return (
    <span
      className="font-[family-name:var(--font-primary)] text-sm font-medium text-gray-500 dark:text-sidebar-foreground"
      style={toneStyle(tone)}
    >
      {value ?? "N/A"}
    </span>
  );
}

export function overviewHeroMiniStats(
  overview: StrategyRdApplyOverview | null,
  currency?: string | null
): HeroCard[] {
  if (!overview) return [];
  const items: HeroCard[] = [];

  // Order: Trades, Sum PnL, Win Rate, Max Drawdown. Ending equity leads the equity
  // card right under these, so it isn't repeated here.
  if (overview.closed_count != null) {
    const exp = formatStrategyRdRMultiple(overview.expectancy_r);
    items.push({
      id: "trades",
      title: "Trades",
      amount: exp ? `${overview.closed_count} · ${exp} avg` : String(overview.closed_count),
      backgroundGradient:
        overview.expectancy_r == null
          ? MINI_STAT_COUNT_GRADIENT
          : overview.expectancy_r >= 0
            ? MINI_STAT_WIN_GRADIENT
            : MINI_STAT_LOSS_GRADIENT,
      tooltip: "Closed trades, and the average result per trade in R (1R = the risk taken)",
    });
  }

  const sumPnl = formatStrategyRdMoney(overview.sum_pnl, currency);
  const retPct = formatStrategyRdPct(overview.return_on_starting_equity_pct);
  if (sumPnl) {
    const n = overview.sum_pnl!;
    items.push({
      id: "sum-pnl",
      title: "Sum PnL",
      amount: retPct ? `${sumPnl} (${retPct})` : sumPnl,
      backgroundGradient: n >= 0 ? MINI_STAT_WIN_GRADIENT : MINI_STAT_LOSS_GRADIENT,
      tooltip: "Total closed PnL with return on starting equity",
    });
  } else {
    const sumR = formatStrategyRdRMultiple(overview.sum_r);
    if (sumR) {
      const n = overview.sum_r!;
      items.push({
        id: "sum-r",
        title: "Sum R",
        amount: sumR,
        backgroundGradient: n >= 0 ? MINI_STAT_WIN_GRADIENT : MINI_STAT_LOSS_GRADIENT,
        tooltip: "Total R across closed trades",
      });
    }
  }

  const win = formatStrategyRdWinRate(overview.win_rate);
  if (win) {
    items.push({
      id: "win-rate",
      title: "Win rate",
      amount: win,
      backgroundGradient: MINI_STAT_COUNT_GRADIENT,
      tooltip: "Share of closed outcomes that hit target",
    });
  }

  const maxDd = formatStrategyRdMoney(overview.max_drawdown, currency);
  const maxDdPct = formatStrategyRdPct(overview.max_drawdown_pct);
  if (maxDd) {
    items.push({
      id: "max-dd",
      title: "Max drawdown",
      amount: maxDdPct ? `${maxDd} (${maxDdPct})` : maxDd,
      backgroundGradient: MINI_STAT_LOSS_GRADIENT,
      tooltip: "Peak-to-trough equity drawdown ($ and % of peak)",
    });
  } else {
    const maxDdR = formatStrategyRdRMultiple(overview.max_drawdown_r);
    if (maxDdR) {
      items.push({
        id: "max-dd-r",
        title: "Max drawdown R",
        amount: maxDdR,
        backgroundGradient: MINI_STAT_LOSS_GRADIENT,
        tooltip: "Peak-to-trough on cumulative R",
      });
    }
  }

  return items.slice(0, 4);
}

/** Entry / exit times of the backtest's trades (for "Time to exit"). */
export type StrategyRdHoldTimeRow = { entry_time?: string | null; exit_time?: string | null };

export function overviewOutcomesRows(
  overview: StrategyRdApplyOverview | null,
  fills?: ReadonlyArray<StrategyRdHoldTimeRow | null | undefined> | null
): MetricRow[] {
  if (!overview) return [];
  const rows: MetricRow[] = [];

  // Win Rate, Matches, TP Hits, SL Hits, Open, streaks, MFE, MAE, Bars
  const win = formatStrategyRdWinRate(overview.win_rate);
  if (win) {
    rows.push({
      id: "win-rate",
      metric: "Win rate",
      value: win,
      tooltip: "Share of closed outcomes that hit target",
    });
  }

  if (overview.match_count != null) {
    rows.push({
      id: "matches",
      metric: "Matches",
      value: String(Math.round(overview.match_count)),
      tooltip: "Matches in this apply run",
    });
  }

  if (overview.target_count != null && Number.isFinite(overview.target_count)) {
    rows.push({
      id: "tp-hits",
      metric: "Take Profit Exits",
      value: String(Math.round(overview.target_count)),
      tooltip: "Matches that exited at take profit first",
    });
  }

  if (overview.stop_count != null && Number.isFinite(overview.stop_count)) {
    rows.push({
      id: "sl-hits",
      metric: "Stop Loss Exits",
      value: String(Math.round(overview.stop_count)),
      tooltip: "Matches that exited at stop loss first",
    });
  }

  if (overview.open_count != null) {
    rows.push({
      id: "open",
      metric: "Open",
      value: String(Math.round(overview.open_count)),
      tooltip: "Neither target nor stop within the scan window",
    });
  }

  if (overview.max_win_streak != null && overview.max_win_streak > 0) {
    rows.push({
      id: "win-streak",
      metric: "Max win streak",
      value: String(Math.round(overview.max_win_streak)),
    });
  }
  if (overview.max_loss_streak != null && overview.max_loss_streak > 0) {
    rows.push({
      id: "loss-streak",
      metric: "Max loss streak",
      value: String(Math.round(overview.max_loss_streak)),
    });
  }

  const unit = overview.excursion_unit;
  const mfe = formatStrategyRdExcursionSummary({
    median: overview.median_mfe,
    avg: overview.avg_mfe,
    min: overview.min_mfe,
    max: overview.max_mfe,
    unit,
  });
  if (mfe) {
    rows.push({
      id: "mfe",
      metric: unit === "atr" ? "MFE (ATR)" : "MFE",
      value: mfe.amount,
      tooltip: mfe.tooltip,
    });
  }
  const mae = formatStrategyRdExcursionSummary({
    median: overview.median_mae,
    avg: overview.avg_mae,
    min: overview.min_mae,
    max: overview.max_mae,
    unit,
  });
  if (mae) {
    rows.push({
      id: "mae",
      metric: unit === "atr" ? "MAE (ATR)" : "MAE",
      value: mae.amount,
      tooltip: mae.tooltip,
    });
  }

  // Time, not bar count: bars are counted on the exit's resolving timeframe, which can be
  // finer than the setup's — the fills' entry / exit times are the truth.
  const timeToExit = formatStrategyRdTimeToExit(fills ?? []);
  const bars = timeToExit
    ? null
    : formatStrategyRdBarsToEvent({
        value: overview.median_bars_to_event ?? overview.avg_bars_to_event,
        min: overview.min_bars_to_event,
        max: overview.max_bars_to_event,
      });
  if (timeToExit) {
    rows.push({
      id: "time_to_exit",
      metric: "Time to exit",
      value: timeToExit,
      tooltip: "Median (min–max) time from entry to take profit or stop",
    });
  } else if (bars) {
    rows.push({
      id: "bars",
      metric: "Bars to exit",
      value: `${bars} bars`,
      tooltip: "Median (min–max) bars to take profit or stop",
    });
  }

  return rows;
}

/** Robustness `return_pct` p5–p95 — shown beside the backtest's return, never instead of it. */
export type StrategyRdReturnRange = { p5: number | null; p95: number | null };

export function strategyRdReturnRangeLabel(range: StrategyRdReturnRange | null | undefined): string | null {
  const lo = formatStrategyRdPct(range?.p5);
  const hi = formatStrategyRdPct(range?.p95);
  return lo && hi ? `Varies on other feeds: ${lo} to ${hi}` : null;
}

export function overviewEconomicsRows(
  overview: StrategyRdApplyOverview | null,
  currency?: string | null,
  margin?: StrategyRdMarginSummary | null,
  returnRange?: StrategyRdReturnRange | null
): MetricRow[] {
  if (!overview) return [];
  const rows: MetricRow[] = [];

  // Ending equity, Sum, Expectancy, PF, Max DD, R:R, Avg/Largest win·loss
  const ending = formatStrategyRdMoney(overview.ending_equity, currency);
  if (ending) {
    rows.push({
      id: "ending-equity",
      metric: "Ending equity",
      usd: ending,
      r: null,
      tooltip: "Equity after closed Research sim fills",
    });
  }

  const sumPnl = formatStrategyRdMoney(overview.sum_pnl, currency);
  const retPct = formatStrategyRdPct(overview.return_on_starting_equity_pct);
  const sumUsd = sumPnl ? (retPct ? `${sumPnl} (${retPct})` : sumPnl) : null;
  const sumR = formatStrategyRdRMultiple(overview.sum_r);
  if (sumUsd || sumR) {
    rows.push({
      id: "sum",
      metric: "Sum / total",
      usd: sumUsd,
      r: sumR,
      usdTone: overview.sum_pnl,
      rTone: overview.sum_r,
      tooltip: "Total across closed trades; USD includes return on starting equity",
    });
  }
  const rangeLabel = strategyRdReturnRangeLabel(returnRange);
  if (rangeLabel) {
    rows.push({
      id: "return-range",
      metric: "Return on other feeds",
      usd: rangeLabel.replace("Varies on other feeds: ", ""),
      r: null,
      tooltip: "Robustness test: return in 90% of runs on data that differs like another feed",
    });
  }

  const expUsdVal = overview.expectancy ?? overview.avg_pnl ?? null;
  const expRVal = overview.expectancy_r ?? overview.avg_r ?? null;
  const expUsd = formatStrategyRdMoney(expUsdVal, currency);
  const expR = formatStrategyRdRMultiple(expRVal);
  if (expUsd || expR) {
    rows.push({
      id: "expectancy",
      metric: "Expectancy",
      usd: expUsd,
      r: expR,
      usdTone: expUsdVal,
      rTone: expRVal,
      tooltip: "Average closed trade (USD and R)",
    });
  }

  const pfUsd =
    overview.profit_factor != null && Number.isFinite(overview.profit_factor)
      ? overview.profit_factor >= 10
        ? overview.profit_factor.toFixed(1)
        : overview.profit_factor.toFixed(2)
      : null;
  const pfR =
    overview.profit_factor_r != null && Number.isFinite(overview.profit_factor_r)
      ? overview.profit_factor_r >= 10
        ? overview.profit_factor_r.toFixed(1)
        : overview.profit_factor_r.toFixed(2)
      : null;
  if (pfUsd || pfR) {
    rows.push({
      id: "pf",
      metric: "Profit factor",
      usd: pfUsd,
      r: pfR,
      tooltip: "Gross wins / gross losses",
    });
  }

  const maxDd = formatStrategyRdMoney(overview.max_drawdown, currency);
  const maxDdPct = formatStrategyRdPct(overview.max_drawdown_pct);
  const ddUsd = maxDd ? (maxDdPct ? `${maxDd} (${maxDdPct})` : maxDd) : null;
  const ddR = formatStrategyRdRMultiple(overview.max_drawdown_r);
  if (ddUsd || ddR) {
    rows.push({
      id: "max-dd",
      metric: "Max drawdown",
      usd: ddUsd,
      r: ddR,
      usdTone: overview.max_drawdown,
      rTone: overview.max_drawdown_r,
      tooltip: "Peak-to-trough drawdown ($ / % of peak, and R)",
    });
  }

  const rrUsd =
    overview.risk_reward_ratio != null && Number.isFinite(overview.risk_reward_ratio)
      ? `${
          overview.risk_reward_ratio >= 10
            ? overview.risk_reward_ratio.toFixed(1)
            : overview.risk_reward_ratio.toFixed(2)
        } : 1`
      : null;
  const rrR = formatStrategyRdRewardRisk(overview.avg_win_r);
  if (rrUsd || rrR) {
    rows.push({
      id: "rr",
      metric: "Risk:reward",
      usd: rrUsd,
      r: rrR,
      tooltip: "Avg win vs |avg loss| (USD) · avg win R vs −1R stop",
    });
  }

  const avgWin = formatStrategyRdMoney(overview.avg_win, currency);
  const avgWinR = formatStrategyRdRMultiple(overview.avg_win_r);
  if (avgWin || avgWinR) {
    rows.push({
      id: "avg-win",
      metric: "Avg win",
      usd: avgWin,
      r: avgWinR,
      usdTone: overview.avg_win,
      rTone: overview.avg_win_r,
    });
  }
  const avgLoss = formatStrategyRdMoney(overview.avg_loss, currency);
  const avgLossR = formatStrategyRdRMultiple(overview.avg_loss_r);
  if (avgLoss || avgLossR) {
    rows.push({
      id: "avg-loss",
      metric: "Avg loss",
      usd: avgLoss,
      r: avgLossR,
      usdTone: overview.avg_loss,
      rTone: overview.avg_loss_r,
    });
  }

  const largestWin = formatStrategyRdMoney(overview.largest_win, currency);
  if (largestWin) {
    rows.push({
      id: "largest-win",
      metric: "Largest win",
      usd: largestWin,
      r: null,
      usdTone: overview.largest_win,
    });
  }
  const largestLoss = formatStrategyRdMoney(overview.largest_loss, currency);
  if (largestLoss) {
    rows.push({
      id: "largest-loss",
      metric: "Largest loss",
      usd: largestLoss,
      r: null,
      usdTone: overview.largest_loss,
    });
  }

  const leverage = formatStrategyRdLeverage(margin?.leverage);
  if (leverage) {
    rows.push({ id: "leverage", metric: "Account leverage", usd: leverage, r: null });
  }
  const peakMargin = formatStrategyRdMoney(margin?.peak_margin, currency);
  if (peakMargin) {
    rows.push({
      id: "peak-margin",
      metric: "Peak margin",
      usd: peakMargin,
      r: null,
      tooltip: "Most margin held by open trades at once (position value ÷ leverage)",
    });
  }
  const minFree = formatStrategyRdMoney(margin?.min_free_margin, currency);
  if (minFree) {
    rows.push({
      id: "min-free-margin",
      metric: "Lowest free margin",
      usd: minFree,
      r: null,
      usdTone: margin?.min_free_margin != null && margin.min_free_margin < 0 ? margin.min_free_margin : null,
      tooltip: "Equity minus margin held, at its lowest. A trade that didn't fit was cut or skipped",
    });
  }

  return rows;
}

/** The Statistics headline tiles (Trade Log mini stats), two or four across. */
export function HeroStrip({ items }: { items: HeroCard[] }): JSX.Element | null {
  if (items.length === 0) return null;
  return (
    // Sized by its own width (the Lab's results column is narrower than the window).
    <div className="@container/heroes">
    <div
      data-mini-stats-grid=""
      data-strategy-rd-overview-heroes=""
      // Four across when there is room, two by two otherwise — never 3 + 1.
      className={cn(
        "grid grid-cols-2 gap-3",
        items.length >= 4 ? "@[60rem]/heroes:grid-cols-4" : "@[46rem]/heroes:grid-cols-3",
        "@[60rem]/heroes:gap-4"
      )}
    >
      {items.map((stat) => (
        <TradeLogMiniStatistics
          key={stat.id}
          title={stat.title}
          amount={stat.amount}
          percentage={null}
          showPercentage={false}
          backgroundGradient={stat.backgroundGradient}
          tooltip={stat.tooltip}
          fitAmount
        />
      ))}
    </div>
    </div>
  );
}

function OutcomesTable({ rows }: { rows: MetricRow[] }): JSX.Element | null {
  const columns: ColumnDef<MetricRow, unknown>[] = useMemo(
    () => [
      {
        accessorKey: "metric",
        header: "Metric",
        enableSorting: false,
        cell: ({ row }) => metricCell(row.original.metric, row.original.tooltip),
      },
      {
        accessorKey: "value",
        header: "Value",
        enableSorting: false,
        cell: ({ row }) => valueCell(row.original.value, row.original.tone),
      },
    ],
    []
  );

  if (rows.length === 0) return null;
  return (
    <div
      data-statistics-shadcn-card=""
      data-strategy-rd-outcomes-table=""
      className={strategyRdSectionCardClassName}
    >
      <h3 className={strategyRdCardTitleClassName}>Outcomes</h3>
      <DataTable columns={columns} data={rows} showPagination={false} emptyMessage="No outcome stats." />
    </div>
  );
}

function EconomicsTable({
  rows,
  currency,
}: {
  rows: MetricRow[];
  currency?: string | null;
}): JSX.Element | null {
  const moneyHeader = strategyRdMoneyCurrency(currency);
  const showUsd = rows.some((r) => r.usd);
  const showR = rows.some((r) => r.r);

  const columns: ColumnDef<MetricRow, unknown>[] = useMemo(() => {
    const cols: ColumnDef<MetricRow, unknown>[] = [
      {
        accessorKey: "metric",
        header: "Metric",
        enableSorting: false,
        cell: ({ row }) => metricCell(row.original.metric, row.original.tooltip),
      },
    ];
    if (showUsd) {
      cols.push({
        id: "usd",
        header: moneyHeader,
        enableSorting: false,
        cell: ({ row }) => valueCell(row.original.usd, row.original.usdTone),
      });
    }
    if (showR) {
      cols.push({
        id: "r",
        header: "R",
        enableSorting: false,
        cell: ({ row }) => valueCell(row.original.r, row.original.rTone),
      });
    }
    return cols;
  }, [moneyHeader, showR, showUsd]);

  if (rows.length === 0) return null;
  return (
    <div
      data-statistics-shadcn-card=""
      data-strategy-rd-economics-table=""
      className={strategyRdSectionCardClassName}
    >
      <h3 className={strategyRdCardTitleClassName}>Economics</h3>
      <DataTable
        columns={columns}
        data={rows}
        showPagination={false}
        emptyMessage="No economics stats."
        tableClassName="[&_[data-slot=table]]:table-fixed [&_[data-slot=table]]:w-full"
      />
    </div>
  );
}

export type StrategyRdOverviewStatsProps = {
  overview: StrategyRdApplyOverview | null;
  showCharts?: boolean;
  /** False when the stat cards are pinned elsewhere (the Lab's results header). */
  showHeroes?: boolean;
  /** The trades behind the overview — turns "Bars to exit" into real holding time. */
  fills?: ReadonlyArray<StrategyRdHoldTimeRow | null | undefined> | null;
  /** Account currency (USD unless the quote couldn't be converted). */
  currency?: string | null;
  /** Account margin stats (whole account only). */
  margin?: StrategyRdMarginSummary | null;
  /** Robustness "sensitive": range across feeds, next to the return. */
  returnRange?: StrategyRdReturnRange | null;
};

/** 4 heroes + Outcomes / Economics Statistics-style tables (+ optional charts). */
/** The headline stat cards (ending equity, net P&L, win rate, drawdown) on their own. */
export function StrategyRdOverviewHeroes({
  overview,
  currency = null,
}: {
  overview: StrategyRdApplyOverview | null;
  currency?: string | null;
}): JSX.Element | null {
  const heroes = useMemo(() => overviewHeroMiniStats(overview, currency), [currency, overview]);
  return <HeroStrip items={heroes} />;
}

export function StrategyRdOverviewStats({
  overview,
  showCharts = true,
  showHeroes = true,
  fills = null,
  currency = null,
  margin = null,
  returnRange = null,
}: StrategyRdOverviewStatsProps): JSX.Element | null {
  const rangeLabel = strategyRdReturnRangeLabel(returnRange);
  const heroes = useMemo(() => overviewHeroMiniStats(overview, currency), [currency, overview]);
  const outcomes = useMemo(() => overviewOutcomesRows(overview, fills), [fills, overview]);
  const economics = useMemo(
    () => overviewEconomicsRows(overview, currency, margin, returnRange),
    [currency, margin, overview, returnRange]
  );

  const hasUsdCharts =
    showCharts &&
    ((overview?.equity_curve?.length ?? 0) > 1 ||
      (overview?.daily_pnl?.length ?? 0) > 0 ||
      (overview?.weekly_pnl?.length ?? 0) > 0);

  if (heroes.length === 0 && outcomes.length === 0 && economics.length === 0) {
    return (
      <p className={strategyRdMetaClassName}>No overview statistics for this run.</p>
    );
  }

  return (
    <div className="flex flex-col gap-4" data-strategy-rd-overview-stats="">
      {showHeroes ? <HeroStrip items={heroes} /> : null}
      {rangeLabel ? (
        <p
          className={`${strategyRdMetaClassName} font-medium`}
          data-strategy-rd-return-range=""
        >
          {rangeLabel} (this backtest {formatStrategyRdPct(overview?.return_on_starting_equity_pct) ?? "N/A"})
        </p>
      ) : null}
      {outcomes.length > 0 || economics.length > 0 ? (
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2 lg:items-start">
          <OutcomesTable rows={outcomes} />
          <EconomicsTable rows={economics} currency={currency} />
        </div>
      ) : null}
      {hasUsdCharts ? <StrategyRdSimCharts overview={overview} currency={currency} /> : null}
    </div>
  );
}
